// School license dates are CALENDAR DAYS in the school's time zone, not UTC
// instants. The admin form used to store "Jan 15" as 2027-01-15T00:00Z and treat
// the end as exclusive, so a New Jersey license ended at 7 pm Eastern on Jan 14
// and the school lost its whole last day.
//
// The rule now:
//   startsAt  = 00:00 school time on the start date
//   expiresAt = 00:00 school time on the day AFTER the end date (exclusive), so
//               access runs through the whole end date
// licenseStatus() already treats expiresAt as exclusive, so it needs no change.
//
// DISPLAY: every surface prints licenseLastDayLabel(expiresAt) worded "through"
// ("full access through Oct 14, 2026"); never format expiresAt directly. The
// stored instant is the moment access STOPS, so printing it names the day after
// the last day — the dashboard, /pricing and the class-join screen all said
// "until Oct 15" for a license /school called "through Oct 14" (QA, 2026-10).
// Compute the label on the server and hand client components the string.
//
// Every school is in New Jersey today. Organization has no time-zone column
// yet; when a school outside Eastern time signs, add one and pass it here.
//
// Pure (Intl only): used by the client admin form as well as the server.

import {
  lastCoveredDateString,
  lastCoveredDayLabel,
  startOfLocalDateUtc,
  userDateString,
  userDateLabel,
} from '@/lib/time/user-day'

export const SCHOOL_TIME_ZONE = 'America/New_York'

// The midnight arithmetic lives in time/user-day.ts (startOfLocalDateUtc), shared
// with comp grants, so a license and a comp picked for the same last day end at
// the same local midnight.

/** The instant a license starts, for a start date picked as YYYY-MM-DD. */
export function licenseStartFromDate(ymd: string, tz = SCHOOL_TIME_ZONE): Date | null {
  return startOfLocalDateUtc(tz, ymd)
}

/** The exclusive end instant for a LAST day picked as YYYY-MM-DD. */
export function licenseEndFromDate(ymd: string, tz = SCHOOL_TIME_ZONE): Date | null {
  return startOfLocalDateUtc(tz, ymd, 1)
}

/** The start date (YYYY-MM-DD, school time) of a stored startsAt. */
export function licenseStartDate(startsAt: Date | string, tz = SCHOOL_TIME_ZONE): string {
  return userDateString(tz, new Date(startsAt))
}

/**
 * The LAST day of access (YYYY-MM-DD, school time) for a stored exclusive
 * expiresAt — the day before it, read in school time.
 */
export function licenseLastDate(expiresAt: Date | string, tz = SCHOOL_TIME_ZONE): string {
  return lastCoveredDateString(tz, expiresAt)
}

/** "Sep 1, 2026" for the start date. */
export function licenseStartLabel(startsAt: Date | string, tz = SCHOOL_TIME_ZONE): string {
  return userDateLabel(tz, new Date(startsAt))
}

/** "Jan 15, 2027" for the last day of access — always worded "through". */
export function licenseLastDayLabel(expiresAt: Date | string, tz = SCHOOL_TIME_ZONE): string {
  return lastCoveredDayLabel(tz, expiresAt)
}
